import { resumeResearch } from "../lib/api";
import { StudyWorkspace } from "./StudyWorkspace";
import type { ScriptureSelection } from "../shared/bible";
import type { ResearchRun } from "../shared/research";
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  Archive,
  BookOpenText,
  Books,
  CaretDown,
  Check,
  Clock,
  Copy,
  DotsThree,
  DotsSixVertical,
  FileDoc,
  ListBullets,
  MagnifyingGlass,
  Moon,
  NotePencil,
  Pause,
  Play,
  Plus,
  Printer,
  Sparkle,
  Sun,
  Trash,
  WarningCircle,
  X,
} from "@phosphor-icons/react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {
  Document,
  HeadingLevel,
  Packer,
  Paragraph,
  TextRun,
} from "docx";
import {
  type FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useNavigate, useParams } from "react-router";
import { MarkdownContent } from "./MarkdownContent";
import { SolaMark } from "./Brand";
import { api, ApiError } from "../lib/api";
import {
  cacheSermon,
  cachedSermon,
  queueSermonMutation,
  queuedSermonMutations,
  removeQueuedSermonMutation,
} from "../lib/sermon-offline";
import {
  bibleBooks,
  bibleVersion,
  expandScripturePassageRange,
  parseScripturePassageRange,
  scripturePassageLabel,
  type ScripturePassageRange,
} from "../shared/bible";
import type { StudyNote } from "../shared/study";
import {
  createSermonBlock,
  createStarterSermonDocument,
  plainTextFromRichText,
  preparationTypes,
  sermonApproaches,
  sermonBlockTypes,
  sermonEstimatedMinutes,
  sermonWordCount,
  type PreparationType,
  type SermonArtifact,
  type SermonAiDraft,
  type SermonAiDraftDepth,
  type SermonAiDraftMode,
  type SermonBlock,
  type SermonBlockType,
  type SermonDocument,
  type SermonRecord,
  type SermonReviewFinding,
  type SermonSeries,
  type SermonStatus,
  type SermonStudyLink,
  type SermonSuggestion,
  type SermonSummary,
} from "../shared/sermons";
import "../sermons.css";

interface SermonUser {
  id: string;
  name: string;
  email: string;
  role: string;
}

interface SermonBilling {
  admin: boolean;
  planKey: string;
  organizations: Array<{
    organizationId: string;
    name: string;
    available: number;
  }>;
}

function sermonPatchPayload(sermon: SermonRecord) {
  return {
    title: sermon.title,
    preparationType: sermon.preparationType,
    seriesId: sermon.seriesId,
    primaryVersionId: sermon.primaryVersionId,
    editionHash: sermon.editionHash,
    primaryReferences: sermon.primaryReferences,
    passageLabel: sermon.passageLabel,
    audience: sermon.audience,
    location: sermon.location,
    speaker: sermon.speaker,
    targetMinutes: sermon.targetMinutes,
    scheduledFor: sermon.scheduledFor,
    approach: sermon.approach,
    topics: sermon.topics,
    status: sermon.status,
    document: sermon.document,
    documentVersion: sermon.documentVersion,
  };
}

interface SermonProps {
  user: SermonUser | null;
  billing?: SermonBilling | null;
  onAuth: () => void;
}

interface SermonDetailResponse {
  sermon: SermonRecord;
  noteLinks: SermonStudyLink[];
  suggestions: SermonSuggestion[];
  artifacts: SermonArtifact[];
  scriptureContexts: Array<Record<string, unknown>>;
  revisions: Array<{
    id: string;
    version: number;
    reason: string;
    created_at: number;
  }>;
  aiDrafts: SermonAiDraft[];
}

const preparationLabels: Record<PreparationType, string> = {
  sermon: "Sermon",
  bible_study: "Bible study",
  sunday_school: "Sunday-school lesson",
  devotional: "Devotional",
  small_group: "Small-group discussion",
  youth_lesson: "Youth lesson",
};

const blockLabels: Record<SermonBlockType, string> = {
  title: "Title",
  big_idea: "Big idea",
  introduction: "Introduction",
  point: "Point",
  subpoint: "Subpoint",
  scripture: "Scripture",
  explanation: "Explanation",
  illustration: "Illustration",
  application: "Application",
  transition: "Transition",
  question: "Question",
  quotation: "Quotation",
  story: "Story",
  prayer: "Prayer",
  invitation: "Invitation",
  private_note: "Private note",
  stage_direction: "Stage direction",
};

const assistantActions = [
  ["clarify", "Clarify"],
  ["shorten", "Shorten"],
  ["strengthen_transition", "Strengthen transition"],
  ["test_big_idea", "Test the big idea"],
  ["biblical_support", "Find biblical support"],
  ["compare_interpretations", "Compare interpretations"],
  ["unsupported_claims", "Identify unsupported claims"],
  ["adapt_audience", "Adapt for audience"],
] as const;

function formatDate(timestamp: number | null): string {
  if (!timestamp) return "No date";
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(timestamp * 1_000));
}

function StatusPill({ status }: { status: SermonStatus }) {
  return <span className={`sermon-status sermon-status-${status}`}>{status}</span>;
}

function SignedOutSermons({ onAuth }: { onAuth: () => void }) {
  return (
    <section className="sermon-empty-auth">
      <BookOpenText size={38} />
      <p className="eyebrow">Sermon preparation</p>
      <h1>Prepare faithfully. Keep the author’s voice.</h1>
      <p>
        Sermon writing, revision history, exports, and preaching mode are private
        to your Bible Alone account.
      </p>
      <button className="primary-button" type="button" onClick={onAuth}>
        Sign in to begin
      </button>
    </section>
  );
}

export function SermonDashboard({ user, onAuth }: SermonProps) {
  const [sermons, setSermons] = useState<SermonSummary[]>([]);
  const [series, setSeries] = useState<SermonSeries[]>([]);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<"active" | SermonStatus>("active");
  const [loading, setLoading] = useState(Boolean(user));
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError("");
    try {
      const search = new URLSearchParams();
      if (status !== "active") search.set("status", status);
      if (query.trim()) search.set("q", query.trim());
      const [sermonResponse, seriesResponse] = await Promise.all([
        api<{ sermons: SermonSummary[] }>(`/api/sermons?${search}`),
        api<{ series: SermonSeries[] }>("/api/sermon-series"),
      ]);
      setSermons(sermonResponse.sermons);
      setSeries(seriesResponse.series);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Sermons could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, [query, status, user]);

  useEffect(() => {
    const timeout = window.setTimeout(() => void load(), 180);
    return () => window.clearTimeout(timeout);
  }, [load]);

  if (!user) return <SignedOutSermons onAuth={onAuth} />;

  const upcoming = sermons.filter(
    (sermon) =>
      sermon.scheduledFor &&
      sermon.scheduledFor * 1_000 >= Date.now() - 86_400_000 &&
      sermon.status !== "archived",
  );
  const drafts = sermons.filter((sermon) => sermon.status === "draft" || sermon.status === "idea");

  return (
    <section className="sermon-dashboard page-shell">
      <header className="sermon-dashboard-header">
        <div>
          <p className="eyebrow">Private preparation workspace</p>
          <h1>Sermons</h1>
          <p>Study the passage, shape the message, and carry a clean preaching view with you.</p>
        </div>
        <Link className="primary-button" to="/sermons/new">
          <Plus size={18} weight="bold" />
          New preparation
        </Link>
      </header>

      <div className="sermon-dashboard-tools">
        <label className="sermon-search">
          <MagnifyingGlass size={18} aria-hidden="true" />
          <span className="sr-only">Search sermons</span>
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search title, passage, topic, or sermon text"
          />
        </label>
        <div className="segmented-control" aria-label="Sermon status filter">
          {(["active", "draft", "ready", "preached", "archived"] as const).map((value) => (
            <button
              className={status === value ? "active" : ""}
              type="button"
              key={value}
              onClick={() => setStatus(value)}
            >
              {value === "active" ? "All active" : value}
            </button>
          ))}
        </div>
      </div>

      {error ? <p className="inline-error">{error}</p> : null}
      {loading ? <div className="sermon-loading">Loading your preparation library…</div> : null}

      {!loading && !sermons.length ? (
        <div className="sermon-zero-state">
          <BookOpenText size={34} />
          <h2>{query ? "No sermons match this search" : "Begin with the passage"}</h2>
          <p>
            {query
              ? "Try another title, passage, topic, or status."
              : "Create a sermon, Bible study, lesson, devotional, or discussion guide."}
          </p>
          {!query ? (
            <Link className="secondary-button" to="/sermons/new">
              Create the first preparation
            </Link>
          ) : null}
        </div>
      ) : null}

      {upcoming.length ? (
        <DashboardSection title="Upcoming" count={upcoming.length}>
          {upcoming.map((sermon) => (
            <SermonCard key={sermon.id} sermon={sermon} onChanged={load} />
          ))}
        </DashboardSection>
      ) : null}

      {drafts.length && status === "active" ? (
        <DashboardSection title="Drafts" count={drafts.length}>
          {drafts.map((sermon) => (
            <SermonCard key={sermon.id} sermon={sermon} onChanged={load} />
          ))}
        </DashboardSection>
      ) : null}

      {sermons.length && (status !== "active" || (!upcoming.length && !drafts.length)) ? (
        <DashboardSection title={status === "active" ? "Preparations" : status} count={sermons.length}>
          {sermons.map((sermon) => (
            <SermonCard key={sermon.id} sermon={sermon} onChanged={load} />
          ))}
        </DashboardSection>
      ) : null}

      {series.length ? (
        <section className="sermon-series-strip">
          <div className="section-heading">
            <div>
              <p className="eyebrow">Series</p>
              <h2>Grouped preparation</h2>
            </div>
          </div>
          <div className="sermon-series-grid">
            {series.map((item) => (
              <article key={item.id}>
                <Books size={22} />
                <div>
                  <strong>{item.title}</strong>
                  <span>{item.sermonCount ?? 0} preparations · {item.status}</span>
                </div>
              </article>
            ))}
          </div>
        </section>
      ) : null}
    </section>
  );
}

function DashboardSection({
  title,
  count,
  children,
}: {
  title: string;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <section className="sermon-dashboard-section">
      <div className="section-heading">
        <h2>{title}</h2>
        <span>{count}</span>
      </div>
      <div className="sermon-card-grid">{children}</div>
    </section>
  );
}

function SermonCard({
  sermon,
  onChanged,
}: {
  sermon: SermonSummary;
  onChanged: () => Promise<void>;
}) {
  const archive = async () => {
    await api(`/api/sermons/${sermon.id}`, {
      method: "PATCH",
      body: JSON.stringify({
        status: sermon.status === "archived" ? "draft" : "archived",
        documentVersion: sermon.documentVersion,
      }),
    });
    await onChanged();
  };
  return (
    <article className="sermon-card">
      <Link to={`/sermons/${sermon.id}`} aria-label={`Open ${sermon.title}`}>
        <div className="sermon-card-topline">
          <span>{preparationLabels[sermon.preparationType]}</span>
          <StatusPill status={sermon.status} />
        </div>
        <h3>{sermon.title}</h3>
        <p className="sermon-passage">{sermon.passageLabel || "Passage not selected"}</p>
        <div className="sermon-card-meta">
          <span><Clock size={15} /> {sermon.estimatedMinutes} min</span>
          <span>{formatDate(sermon.scheduledFor)}</span>
        </div>
        {sermon.seriesTitle ? <p className="sermon-series-name">{sermon.seriesTitle}</p> : null}
      </Link>
      <button type="button" className="icon-button" onClick={() => void archive()}>
        <Archive size={18} />
        <span className="sr-only">
          {sermon.status === "archived" ? "Restore sermon" : "Archive sermon"}
        </span>
      </button>
    </article>
  );
}

function ScriptureRangePicker({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [referenceIndex, setReferenceIndex] = useState<number[][] | null>(null);
  const initial = parseScripturePassageRange(value) ?? {
    start: { book: "John", bookIndex: 42, chapter: 15, verse: 1 },
    end: { book: "John", bookIndex: 42, chapter: 15, verse: 11 },
  };
  const [bookIndex, setBookIndex] = useState(initial.start.bookIndex);
  const [startChapter, setStartChapter] = useState(initial.start.chapter);
  const [startVerse, setStartVerse] = useState(initial.start.verse);
  const [endChapter, setEndChapter] = useState(initial.end.chapter);
  const [endVerse, setEndVerse] = useState(initial.end.verse);

  useEffect(() => {
    void api<{ books: number[][] }>("/api/bible/reference-index/kjv")
      .then((response) => setReferenceIndex(response.books))
      .catch(() => setReferenceIndex(null));
  }, []);

  useEffect(() => {
    if (!open) return;
    const parsed = parseScripturePassageRange(value);
    if (!parsed) return;
    setBookIndex(parsed.start.bookIndex);
    setStartChapter(parsed.start.chapter);
    setStartVerse(parsed.start.verse);
    setEndChapter(parsed.end.chapter);
    setEndVerse(parsed.end.verse);
  }, [open, value]);

  const chapters = referenceIndex?.[bookIndex] ?? [];
  const startVerses = chapters[startChapter - 1] ?? 1;
  const endVerses = chapters[endChapter - 1] ?? 1;
  const candidate: ScripturePassageRange = {
    start: {
      book: bibleBooks[bookIndex]!,
      bookIndex,
      chapter: startChapter,
      verse: startVerse,
    },
    end: {
      book: bibleBooks[bookIndex]!,
      bookIndex,
      chapter: endChapter,
      verse: endVerse,
    },
  };
  let preview = "";
  let pickerError = "";
  try {
    const expanded = expandScripturePassageRange(
      candidate,
      chapters,
    );
    preview = `${expanded.label} \u00b7 ${expanded.verseCount} verse${expanded.verseCount === 1 ? "" : "s"}`;
  } catch (reason) {
    pickerError = reason instanceof Error ? reason.message : "Choose a valid passage.";
  }

  const chooseBook = (nextBookIndex: number) => {
    setBookIndex(nextBookIndex);
    setStartChapter(1);
    setStartVerse(1);
    setEndChapter(1);
    setEndVerse(1);
  };
  const chooseStartChapter = (chapter: number) => {
    setStartChapter(chapter);
    setStartVerse(1);
    if (endChapter < chapter) {
      setEndChapter(chapter);
      setEndVerse(1);
    }
  };
  const chooseEndChapter = (chapter: number) => {
    setEndChapter(chapter);
    setEndVerse(1);
  };

  return (
    <>
      <button type="button" className="secondary-button scripture-picker-trigger" onClick={() => setOpen(true)}>
        <BookOpenText size={18} /> Choose passage
      </button>
      {open ? (
        <div className="scripture-picker-backdrop" role="presentation" onMouseDown={() => setOpen(false)}>
          <section
            className="scripture-range-picker"
            role="dialog"
            aria-modal="true"
            aria-labelledby="scripture-range-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <header>
              <div>
                <p className="eyebrow">Primary Scripture</p>
                <h2 id="scripture-range-title">Choose a passage range</h2>
              </div>
              <button type="button" className="icon-button" onClick={() => setOpen(false)} aria-label="Close passage picker">
                <X size={20} />
              </button>
            </header>
            <label>
              <span>Translation</span>
              <select value="kjv" disabled aria-describedby="sermon-translation-note">
                <option value="kjv">KJV — included</option>
              </select>
              <small id="sermon-translation-note">ESV and NKJV are coming after publisher licensing.</small>
            </label>
            <label>
              <span>Book</span>
              <select value={bookIndex} onChange={(event) => chooseBook(Number(event.target.value))}>
                {bibleBooks.map((book, index) => <option key={book} value={index}>{book}</option>)}
              </select>
            </label>
            <div className="scripture-picker-grid">
              <fieldset>
                <legend>Start</legend>
                <label>
                  <span>Chapter</span>
                  <select value={startChapter} onChange={(event) => chooseStartChapter(Number(event.target.value))}>
                    {chapters.map((_, index) => <option key={index + 1} value={index + 1}>{index + 1}</option>)}
                  </select>
                </label>
                <label>
                  <span>Verse</span>
                  <select value={Math.min(startVerse, startVerses)} onChange={(event) => setStartVerse(Number(event.target.value))}>
                    {Array.from({ length: startVerses }, (_, index) => <option key={index + 1} value={index + 1}>{index + 1}</option>)}
                  </select>
                </label>
              </fieldset>
              <fieldset>
                <legend>End</legend>
                <label>
                  <span>Chapter</span>
                  <select value={endChapter} onChange={(event) => chooseEndChapter(Number(event.target.value))}>
                    {chapters.map((_, index) => <option key={index + 1} value={index + 1}>{index + 1}</option>)}
                  </select>
                </label>
                <label>
                  <span>Verse</span>
                  <select value={Math.min(endVerse, endVerses)} onChange={(event) => setEndVerse(Number(event.target.value))}>
                    {Array.from({ length: endVerses }, (_, index) => <option key={index + 1} value={index + 1}>{index + 1}</option>)}
                  </select>
                </label>
              </fieldset>
            </div>
            <div className={`scripture-picker-preview ${pickerError ? "has-error" : ""}`} aria-live="polite">
              {pickerError || preview || "Loading the KJV passage index\u2026"}
            </div>
            <footer>
              <button type="button" className="ghost-button" onClick={() => setOpen(false)}>Cancel</button>
              <button
                type="button"
                className="primary-button"
                disabled={!referenceIndex || Boolean(pickerError)}
                onClick={() => {
                  onChange(scripturePassageLabel(candidate));
                  setOpen(false);
                }}
              >
                Use this passage
              </button>
            </footer>
          </section>
        </div>
      ) : null}
    </>
  );
}

export function NewSermon({ user, billing, onAuth }: SermonProps) {
  const navigate = useNavigate();
  const [series, setSeries] = useState<SermonSeries[]>([]);
  const [title, setTitle] = useState("");
  const [passage, setPassage] = useState("");
  const [referenceIndex, setReferenceIndex] = useState<number[][] | null>(null);
  const [startWith, setStartWith] = useState<"blank" | "quick" | "deep">("blank");
  const [draftDepth, setDraftDepth] = useState<SermonAiDraftDepth>("full");
  const [researchFocus, setResearchFocus] = useState("");
  const [preparationType, setPreparationType] = useState<PreparationType>("sermon");
  const [approach, setApproach] = useState<(typeof sermonApproaches)[number]>("expository");
  const [audience, setAudience] = useState("");
  const [speaker, setSpeaker] = useState(user?.name ?? "");
  const [location, setLocation] = useState("");
  const [targetMinutes, setTargetMinutes] = useState(30);
  const [scheduledDate, setScheduledDate] = useState("");
  const [topics, setTopics] = useState("");
  const [seriesId, setSeriesId] = useState("");
  const [newSeriesTitle, setNewSeriesTitle] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!user) return;
    void api<{ series: SermonSeries[] }>("/api/sermon-series").then((response) =>
      setSeries(response.series),
    );
  }, [user]);

  useEffect(() => {
    void api<{ books: number[][] }>("/api/bible/reference-index/kjv")
      .then((response) => setReferenceIndex(response.books))
      .catch(() => setReferenceIndex(null));
  }, []);

  if (!user) return <SignedOutSermons onAuth={onAuth} />;

  const createSeries = async () => {
    if (!newSeriesTitle.trim()) return;
    const response = await api<{ series: SermonSeries }>("/api/sermon-series", {
      method: "POST",
      body: JSON.stringify({ title: newSeriesTitle, description: "" }),
    });
    setSeries((current) => [response.series, ...current]);
    setSeriesId(response.series.id);
    setNewSeriesTitle("");
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError("");
    const primaryPassage = parseScripturePassageRange(passage);
    if (!primaryPassage || !referenceIndex) {
      setError("Choose or enter an exact passage such as Job 1:1\u20133:26.");
      return;
    }
    let expanded;
    try {
      expanded = expandScripturePassageRange(
        primaryPassage,
        referenceIndex[primaryPassage.start.bookIndex] ?? [],
      );
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Choose a valid passage.");
      return;
    }
    setSaving(true);
    try {
      const kjv = bibleVersion("kjv");
      const response = await api<{ sermon: SermonRecord }>("/api/sermons", {
        method: "POST",
        body: JSON.stringify({
          title,
          preparationType,
          seriesId: seriesId || null,
          primaryVersionId: "kjv",
          editionHash: kjv.editionHash,
          primaryPassage,
          primaryReferences: expanded.ranges,
          passageLabel: expanded.label,
          audience,
          location,
          speaker,
          targetMinutes,
          scheduledFor: scheduledDate
            ? Math.floor(new Date(`${scheduledDate}T12:00:00`).getTime() / 1_000)
            : null,
          approach,
          topics: topics
            .split(",")
            .map((topic) => topic.trim())
            .filter(Boolean),
          status: "draft",
          document: createStarterSermonDocument(title),
        }),
      });
      await cacheSermon(response.sermon);
      if (startWith !== "blank") {
        window.sessionStorage.setItem(
          `sermon-start:${response.sermon.id}`,
          JSON.stringify({
            mode: startWith,
            depth: draftDepth,
            researchFocus,
            organizationId: billing?.organizations[0]?.organizationId,
          }),
        );
      }
      navigate(`/sermons/${response.sermon.id}`);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "The preparation could not be created.");
    } finally {
      setSaving(false);
    }
  };

  let passagePreview = "";
  let passageValidationError = "";
  if (passage.trim() && referenceIndex) {
    const parsedPassage = parseScripturePassageRange(passage);
    if (!parsedPassage) {
      passageValidationError = "Use an exact range such as John 15:1-11 or Job 1:1\u20133:26.";
    } else {
      try {
        const result = expandScripturePassageRange(
          parsedPassage,
          referenceIndex[parsedPassage.start.bookIndex] ?? [],
        );
        passagePreview = `${result.label} \u00b7 ${result.verseCount} verse${result.verseCount === 1 ? "" : "s"}`;
      } catch (reason) {
        passageValidationError = reason instanceof Error ? reason.message : "Choose a valid passage.";
      }
    }
  }

  return (
    <section className="sermon-new page-shell">
      <Link className="sermon-back-link" to="/sermons">
        <ArrowLeft size={18} /> Sermons
      </Link>
      <header>
        <p className="eyebrow">Passage-first preparation</p>
        <h1>Create a new preparation</h1>
        <p>Set the frame now. You can refine every detail in the builder.</p>
      </header>
      <form onSubmit={(event) => void submit(event)} className="sermon-create-form">
        <div className="sermon-form-primary">
          <label>
            <span>Preparation type</span>
            <select
              value={preparationType}
              onChange={(event) => setPreparationType(event.target.value as PreparationType)}
            >
              {preparationTypes.map((value) => (
                <option key={value} value={value}>{preparationLabels[value]}</option>
              ))}
            </select>
          </label>
          <label>
            <span>Title</span>
            <input
              required
              maxLength={180}
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="Abide in the True Vine"
            />
          </label>
          <label>
            <span>Primary passage</span>
            <div className="scripture-manual-row">
              <input
                required
                value={passage}
                onChange={(event) => setPassage(event.target.value)}
                placeholder="Job 1:1–3:26"
              />
              <ScriptureRangePicker value={passage} onChange={setPassage} />
            </div>
            {passagePreview ? <small className="scripture-entry-preview">{passagePreview}</small> : null}
            {passageValidationError ? <small className="scripture-entry-error">{passageValidationError}</small> : null}
            <small>KJV is the default. ESV and NKJV remain unavailable until licensed.</small>
          </label>
          <fieldset className="sermon-start-options">
            <legend>Start with</legend>
            {([
              ["blank", "Blank sermon", "Open the current free starter blocks."],
              ["quick", "Sola Draft", "Use Terra to prepare a reviewable head start."],
              ["deep", "Deep Research + Draft", "Max-only Sol research with a saved source report."],
            ] as const).map(([value, label, description]) => (
              <label key={value} className={startWith === value ? "selected" : ""}>
                <input
                  type="radio"
                  name="start-with"
                  value={value}
                  checked={startWith === value}
                  onChange={() => setStartWith(value)}
                />
                <span><strong>{label}</strong><small>{description}</small></span>
              </label>
            ))}
          </fieldset>
          {startWith !== "blank" ? (
            <div className="sermon-ai-start-settings">
              <label>
                <span>Draft depth</span>
                <select value={draftDepth} onChange={(event) => setDraftDepth(event.target.value as SermonAiDraftDepth)}>
                  <option value="full">Full first draft</option>
                  <option value="outline">Detailed outline</option>
                </select>
              </label>
              <label>
                <span>Optional research focus</span>
                <textarea
                  value={researchFocus}
                  onChange={(event) => setResearchFocus(event.target.value)}
                  maxLength={2_000}
                  placeholder="Historical setting, a doctrinal question, audience needs…"
                />
              </label>
              <p className="sermon-ai-review-note">
                Sola creates a proposal only. You will review, edit, select, or discard every block before it enters the sermon.
              </p>
            </div>
          ) : null}
        </div>

        <details className="sermon-optional-details">
          <summary>
            <span>
              <strong>Add preparation details</strong>
              <small>Approach, audience, timing, speaker, topics, and series</small>
            </span>
            <CaretDown size={18} />
          </summary>
          <div className="sermon-form-grid">
            <label>
              <span>Approach</span>
              <select value={approach} onChange={(event) => setApproach(event.target.value as typeof approach)}>
                {sermonApproaches.map((value) => <option key={value}>{value}</option>)}
              </select>
            </label>
            <label>
              <span>Audience</span>
              <input value={audience} onChange={(event) => setAudience(event.target.value)} placeholder="Sunday morning congregation" />
            </label>
            <label>
              <span>Target duration</span>
              <input type="number" min={1} max={600} value={targetMinutes} onChange={(event) => setTargetMinutes(Number(event.target.value))} />
            </label>
            <label>
              <span>Speaker</span>
              <input value={speaker} onChange={(event) => setSpeaker(event.target.value)} />
            </label>
            <label>
              <span>Location</span>
              <input value={location} onChange={(event) => setLocation(event.target.value)} placeholder="Grace Community Church" />
            </label>
            <label>
              <span>Date</span>
              <input type="date" value={scheduledDate} onChange={(event) => setScheduledDate(event.target.value)} />
            </label>
            <label className="sermon-form-wide">
              <span>Topics</span>
              <input value={topics} onChange={(event) => setTopics(event.target.value)} placeholder="abiding, fruitfulness, union with Christ" />
            </label>
            <label>
              <span>Series</span>
              <select value={seriesId} onChange={(event) => setSeriesId(event.target.value)}>
                <option value="">No series</option>
                {series.map((item) => <option key={item.id} value={item.id}>{item.title}</option>)}
              </select>
            </label>
            <div className="sermon-inline-series">
              <label>
                <span>New series</span>
                <input value={newSeriesTitle} onChange={(event) => setNewSeriesTitle(event.target.value)} placeholder="The Upper Room" />
              </label>
              <button type="button" className="secondary-button" onClick={() => void createSeries()}>
                Add
              </button>
            </div>
          </div>
        </details>
        {error ? <p className="inline-error">{error}</p> : null}
        <footer>
          <Link className="ghost-button" to="/sermons">Cancel</Link>
          <button className="primary-button" disabled={saving} type="submit">
            {saving ? "Creating…" : "Open the builder"}
            <ArrowRight size={18} />
          </button>
        </footer>
      </form>
    </section>
  );
}

function SortableSermonBlock({
  block,
  selected,
  onSelect,
  onChange,
  onDuplicate,
  onDelete,
  onMove,
}: {
  block: SermonBlock;
  selected: boolean;
  onSelect: () => void;
  onChange: (block: SermonBlock) => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onMove: (direction: -1 | 1) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: block.id });
  const editor = useEditor({
    extensions: [StarterKit],
    content: block.content,
    immediatelyRender: false,
    onUpdate: ({ editor: current }) =>
      onChange({ ...block, content: current.getJSON() as SermonBlock["content"] }),
    editorProps: {
      attributes: {
        "aria-label": `${blockLabels[block.type]} content`,
      },
    },
  });

  useEffect(() => {
    if (!editor || editor.isFocused) return;
    const current = JSON.stringify(editor.getJSON());
    const next = JSON.stringify(block.content);
    if (current !== next) editor.commands.setContent(block.content);
  }, [block.content, editor]);

  return (
    <article
      ref={setNodeRef}
      className={`sermon-block sermon-block-${block.type} ${selected ? "selected" : ""} ${isDragging ? "dragging" : ""}`}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      onClick={onSelect}
    >
      <header>
        <button
          className="drag-handle"
          type="button"
          aria-label={`Reorder ${blockLabels[block.type]} block`}
          {...attributes}
          {...listeners}
        >
          <DotsSixVertical size={19} />
        </button>
        <span className="sermon-block-label">{blockLabels[block.type]}</span>
        <span className={`provenance provenance-${block.provenance}`}>
          {block.provenance === "ai_suggestion" ? "AI accepted" : block.provenance.replaceAll("_", " ")}
        </span>
        <div className="sermon-block-actions">
          <button type="button" onClick={(event) => { event.stopPropagation(); onMove(-1); }} aria-label="Move block up"><ArrowUp size={15} /></button>
          <button type="button" onClick={(event) => { event.stopPropagation(); onMove(1); }} aria-label="Move block down"><ArrowDown size={15} /></button>
          <button type="button" onClick={(event) => { event.stopPropagation(); onDuplicate(); }} aria-label="Duplicate block"><Copy size={15} /></button>
          <button type="button" onClick={(event) => { event.stopPropagation(); onDelete(); }} aria-label="Delete block"><Trash size={15} /></button>
        </div>
      </header>
      <div className={block.collapsed ? "sermon-block-content collapsed" : "sermon-block-content"}>
        <EditorContent editor={editor} />
      </div>
      <footer>
        <label>
          <input
            type="checkbox"
            checked={block.includeInPreaching}
            onChange={(event) => onChange({ ...block, includeInPreaching: event.target.checked })}
          />
          Preaching view
        </label>
        <label>
          Minutes
          <input
            type="number"
            min={0}
            max={600}
            step={0.5}
            value={block.manualMinutes ?? ""}
            onChange={(event) =>
              onChange({
                ...block,
                manualMinutes: event.target.value ? Number(event.target.value) : null,
              })
            }
          />
        </label>
        <button type="button" onClick={() => onChange({ ...block, collapsed: !block.collapsed })}>
          <CaretDown size={14} className={block.collapsed ? "rotated" : ""} />
          {block.collapsed ? "Expand" : "Collapse"}
        </button>
      </footer>
    </article>
  );
}

export function SermonBuilder({ user, billing, onAuth }: SermonProps) {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<SermonDetailResponse | null>(null);
  const [activeMobileTab, setActiveMobileTab] = useState<"study" | "sermon" | "sola">("sermon");
  const [view, setView] = useState<"blocks" | "manuscript" | "outline">("blocks");
  const [selectedBlockIds, setSelectedBlockIds] = useState<string[]>([]);
  const [dirty, setDirty] = useState(false);
  const [saveState, setSaveState] = useState<"saved" | "saving" | "offline" | "conflict">("saved");
  const [error, setError] = useState("");
  const [addMenu, setAddMenu] = useState(false);
  const [notes, setNotes] = useState<StudyNote[]>([]);
  const [noteQuery, setNoteQuery] = useState("");
  const [assistantPrompt, setAssistantPrompt] = useState("");
  const [assistantMessages, setAssistantMessages] = useState<Array<{ role: "user" | "assistant"; content: string; researchRun?:ResearchRun }>>([]);
  const [assistantBusy, setAssistantBusy] = useState(false);
  const [assistantStage,setAssistantStage]=useState('');
  const [assistantRunId,setAssistantRunId]=useState<string|null>(null);
  const assistantAbort=useRef<AbortController|null>(null);

  const [reviewFindings, setReviewFindings] = useState<SermonReviewFinding[]>([]);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [organizationId, setOrganizationId] = useState("");
  const [draftBusy, setDraftBusy] = useState(false);
  const [sermonResearchId,setSermonResearchId]=useState<string|undefined>();
  const [assistantResearch,setAssistantResearch]=useState<ResearchRun|null>(null);
  const [draftSelections, setDraftSelections] = useState<Record<string, string[]>>({});
  const [draftEdits, setDraftEdits] = useState<Record<string, Record<string, string>>>({});
  const [draftProgress, setDraftProgress] = useState<Record<string, string>>({});
  const saveTimer = useRef<number | undefined>(undefined);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const load = useCallback(async () => {
    if (!user || !id) return;
    setError("");
    try {
      const response = await api<SermonDetailResponse>(`/api/sermons/${id}`);
      setDetail(response);
      await cacheSermon(response.sermon);
      setSaveState("saved");
    } catch (reason) {
      const cached = await cachedSermon(id);
      if (cached) {
        setDetail({
          sermon: cached,
          noteLinks: [],
          suggestions: [],
          artifacts: [],
          scriptureContexts: [],
          revisions: [],
          aiDrafts: [],
        });
        setSaveState("offline");
      } else {
        setError(reason instanceof Error ? reason.message : "The sermon could not be loaded.");
      }
    }
  }, [id, user]);

  useEffect(() => {
    void load();
  }, [load]);

  const startAiDraft = useCallback(async (
    mode: SermonAiDraftMode,
    depth: SermonAiDraftDepth,
    researchFocus = "",
    selectedOrganizationId?: string,
  ) => {
    if (!detail) return;
    setDraftBusy(true);
    setError("");
    try {
      const response = await api<{ draft: SermonAiDraft }>(
        `/api/sermons/${detail.sermon.id}/ai-drafts`,
        {
          method: "POST",
          body: JSON.stringify({
            mode,
            depth,
            researchFocus,
            organizationId: mode === "quick" ? selectedOrganizationId : undefined,
            documentVersion: detail.sermon.documentVersion,
          }),
        },
      );
      setDetail((current) =>
        current
          ? {
              ...current,
              aiDrafts: [
                response.draft,
                ...current.aiDrafts.filter((draft) => draft.id !== response.draft.id),
              ],
            }
          : current,
      );
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Sola could not start this proposal.");
    } finally {
      setDraftBusy(false);
    }
  }, [detail]);

  useEffect(() => {
    if (!detail) return;
    const key = `sermon-start:${detail.sermon.id}`;
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return;
    window.sessionStorage.removeItem(key);
    try {
      const request = JSON.parse(raw) as {
        mode: SermonAiDraftMode;
        depth: SermonAiDraftDepth;
        researchFocus?: string;
        organizationId?: string;
      };
      void startAiDraft(
        request.mode,
        request.depth,
        request.researchFocus ?? "",
        request.organizationId,
      );
    } catch {
      setError("The sermon was created, but its Sola starter settings could not be read.");
    }
  }, [detail?.sermon.id, startAiDraft]);

  useEffect(() => {
    if (!detail) return;
    const active = detail.aiDrafts.filter((draft) =>
      ["queued", "generating"].includes(draft.status),
    );
    if (!active.length) return;
    const poll = async () => {
      await Promise.all(active.map(async (draft) => {
        try {
          const response = await api<{
            draft: SermonAiDraft;
            research?: { progress?: number; progressLabel?: string } | null;
          }>(`/api/sermons/${detail.sermon.id}/ai-drafts/${draft.id}`);
          setDetail((current) =>
            current
              ? {
                  ...current,
                  aiDrafts: current.aiDrafts.map((item) =>
                    item.id === response.draft.id ? response.draft : item,
                  ),
                }
              : current,
          );
          if (response.research?.progressLabel) {
            setDraftProgress((current) => ({
              ...current,
              [draft.id]: `${response.research?.progressLabel}`,
            }));
          }
        } catch {
          // The durable card stays visible; a later poll can recover.
        }
      }));
    };
    const timer = window.setInterval(() => void poll(), 3_000);
    void poll();
    return () => window.clearInterval(timer);
  }, [detail?.sermon.id, detail?.aiDrafts.map((draft) => `${draft.id}:${draft.status}`).join("|")]);

  useEffect(() => {
    if (!user) return;
    void api<{ notes: StudyNote[] }>(
      `/api/study/notes${noteQuery.trim() ? `?query=${encodeURIComponent(noteQuery.trim())}` : ""}`,
    )
      .then((response) => setNotes(response.notes))
      .catch(() => setNotes([]));
  }, [noteQuery, user]);

  const updateLocal = useCallback((updater: (sermon: SermonRecord) => SermonRecord) => {
    setDetail((current) => {
      if (!current) return current;
      const sermon = updater(current.sermon);
      void cacheSermon(sermon);
      return { ...current, sermon };
    });
    setDirty(true);
  }, []);

  useEffect(() => {
    if (!dirty || !detail) return;
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(async () => {
      const sermon = detail.sermon;
      setSaveState("saving");
      try {
        const response = await api<{ sermon: SermonRecord }>(`/api/sermons/${sermon.id}`, {
          method: "PATCH",
          body: JSON.stringify(sermonPatchPayload(sermon)),
        });
        setDetail((current) => (current ? { ...current, sermon: response.sermon } : current));
        await cacheSermon(response.sermon);
        setDirty(false);
        setSaveState("saved");
      } catch (reason) {
        if (reason instanceof ApiError && reason.status === 409) {
          setSaveState("conflict");
          await queueSermonMutation(sermon.id, sermonPatchPayload(sermon));
        } else {
          setSaveState("offline");
          await queueSermonMutation(sermon.id, sermonPatchPayload(sermon));
        }
      }
    }, 900);
    return () => {
      if (saveTimer.current) window.clearTimeout(saveTimer.current);
    };
  }, [detail, dirty]);

  const flushQueuedChanges = useCallback(async () => {
    if (!detail || !navigator.onLine) return;
    const queued = (await queuedSermonMutations(detail.sermon.id))
      .sort((left, right) => left.createdAt - right.createdAt);
    const latest = queued.at(-1);
    if (!latest) return;

    const pendingSermon = {
      ...detail.sermon,
      ...latest.payload,
      id: detail.sermon.id,
    } as SermonRecord;

    try {
      setSaveState("saving");
      const response = await api<{ sermon: SermonRecord }>(
        `/api/sermons/${detail.sermon.id}`,
        {
          method: "PATCH",
          body: JSON.stringify(latest.payload),
        },
      );
      await Promise.all(queued.map((mutation) => removeQueuedSermonMutation(mutation.id)));
      setDetail((current) => (current ? { ...current, sermon: response.sermon } : current));
      await cacheSermon(response.sermon);
      setDirty(false);
      setSaveState("saved");
    } catch (reason) {
      setDetail((current) => (current ? { ...current, sermon: pendingSermon } : current));
      await cacheSermon(pendingSermon);
      if (reason instanceof ApiError && reason.status === 409) {
        await Promise.all(queued.map((mutation) => removeQueuedSermonMutation(mutation.id)));
        setSaveState("conflict");
      } else {
        setSaveState("offline");
      }
    }
  }, [detail]);

  useEffect(() => {
    const handleOnline = () => {
      void flushQueuedChanges();
    };
    window.addEventListener("online", handleOnline);
    if (navigator.onLine) void flushQueuedChanges();
    return () => window.removeEventListener("online", handleOnline);
  }, [flushQueuedChanges]);

  const monitorAssistant=async(runId:string,signal:AbortSignal)=>{
    setAssistantRunId(runId);setAssistantBusy(true);
    try{
      await resumeResearch(runId,event=>{
        if(event.progressLabel)setAssistantStage(event.progressLabel);
        if(event.researchRun){const run=event.researchRun;setAssistantResearch(run);setAssistantMessages(current=>[...current.filter(message=>message.researchRun?.id!==run.id),{role:'assistant',content:run.answer??'',researchRun:run}]);}
      },signal);
      const response=await api<SermonDetailResponse>(`/api/sermons/${id}`);
      setDetail(current=>current?{...current,suggestions:response.suggestions}:current);
    }catch(reason){if(!signal.aborted)setError(reason instanceof Error?reason.message:'Sola could not respond.');}
    finally{if(!signal.aborted){setAssistantBusy(false);setAssistantRunId(null);}}
  };

  useEffect(()=>{
    if(!user)return;
    const controller=new AbortController();assistantAbort.current=controller;
    void api<{runs:Array<{id:string;status:string;answer:string|null;prompt:string}>}>(`/api/sermons/${id}/research`,{signal:controller.signal}).then(async history=>{
      const completed=history.runs.filter(run=>run.status==='complete').reverse();
      const runs=await Promise.all(completed.map(run=>api<ResearchRun>(`/api/research/runs/${run.id}`,{signal:controller.signal})));
      if(controller.signal.aborted)return;
      setAssistantMessages(completed.flatMap((record,index)=>[{role:'user' as const,content:record.prompt},{role:'assistant' as const,content:record.answer??'',researchRun:runs[index]!}]));
      setAssistantResearch(runs.at(-1)??null);
      const active=history.runs.find(run=>!['complete','failed','canceled'].includes(run.status));
      if(active)await monitorAssistant(active.id,controller.signal);
    }).catch(reason=>{if(!controller.signal.aborted)setError(String(reason));});
    return()=>controller.abort();
  },[id,user?.id]);

  if (!user) return <SignedOutSermons onAuth={onAuth} />;
  if (!detail && error) return <section className="sermon-builder-error"><WarningCircle size={30} /><h1>Unable to open sermon</h1><p>{error}</p><Link to="/sermons">Return to sermons</Link></section>;
  if (!detail) return <div className="sermon-loading">Opening the sermon builder…</div>;

  const sermon = detail.sermon;
  const wordCount = sermonWordCount(sermon.document);
  const estimatedMinutes = sermonEstimatedMinutes(sermon.document);

  const updateDocument = (document: SermonDocument) =>
    updateLocal((current) => ({ ...current, document }));

  const changeBlock = (nextBlock: SermonBlock) =>
    updateDocument({
      ...sermon.document,
      blocks: sermon.document.blocks.map((block) =>
        block.id === nextBlock.id ? nextBlock : block,
      ),
    });

  const addBlock = (type: SermonBlockType) => {
    const selectedIndex = sermon.document.blocks.findIndex((block) =>
      selectedBlockIds.includes(block.id),
    );
    const blocks = [...sermon.document.blocks];
    blocks.splice(
      selectedIndex >= 0 ? selectedIndex + 1 : blocks.length,
      0,
      createSermonBlock(type),
    );
    updateDocument({ ...sermon.document, blocks });
    setAddMenu(false);
  };

  const moveBlock = (blockId: string, direction: -1 | 1) => {
    const index = sermon.document.blocks.findIndex((block) => block.id === blockId);
    const next = index + direction;
    if (index < 0 || next < 0 || next >= sermon.document.blocks.length) return;
    updateDocument({
      ...sermon.document,
      blocks: arrayMove(sermon.document.blocks, index, next),
    });
  };

  const dragEnd = (event: DragEndEvent) => {
    if (!event.over || event.active.id === event.over.id) return;
    const from = sermon.document.blocks.findIndex((block) => block.id === event.active.id);
    const to = sermon.document.blocks.findIndex((block) => block.id === event.over?.id);
    if (from >= 0 && to >= 0) {
      updateDocument({ ...sermon.document, blocks: arrayMove(sermon.document.blocks, from, to) });
    }
  };

  const saveRevision = async (reason: "manual" | "export" = "manual") => {
    if (dirty) {
      setError("Wait for autosave to finish before creating a revision.");
      return;
    }
    await api(`/api/sermons/${sermon.id}/revisions`, {
      method: "POST",
      body: JSON.stringify({ reason, metadata: { wordCount, estimatedMinutes } }),
    });
    setSaveState("saved");
  };

  const saveConflictCopy = async () => {
    const response = await api<{ sermon: SermonRecord }>("/api/sermons", {
      method: "POST",
      body: JSON.stringify({
        title: `${sermon.title} — conflict copy`,
        preparationType: sermon.preparationType,
        seriesId: sermon.seriesId,
        primaryVersionId: sermon.primaryVersionId,
        editionHash: sermon.editionHash,
        primaryReferences: sermon.primaryReferences,
        passageLabel: sermon.passageLabel,
        audience: sermon.audience,
        location: sermon.location,
        speaker: sermon.speaker,
        targetMinutes: sermon.targetMinutes,
        scheduledFor: sermon.scheduledFor,
        approach: sermon.approach,
        topics: sermon.topics,
        status: "draft",
        document: sermon.document,
      }),
    });
    navigate(`/sermons/${response.sermon.id}`);
  };

  const askSola = async (action: (typeof assistantActions)[number][0] | "ask" = "ask",promptOverride?: string) => {
    const prompt=promptOverride??assistantPrompt.trim();if(!prompt||assistantBusy)return;
    const controller=new AbortController();assistantAbort.current?.abort();assistantAbort.current=controller;
    setAssistantBusy(true);setAssistantStage('Reading the passage');setError('');
    setAssistantMessages(current=>[...current,{role:'user',content:prompt}]);setAssistantPrompt('');
    try{
      const response=await api<{runId:string}>(`/api/sermons/${sermon.id}/assistant`,{method:'POST',body:JSON.stringify({prompt,action,blockIds:selectedBlockIds,organizationId:organizationId||undefined}),signal:controller.signal});
      await monitorAssistant(response.runId,controller.signal);
    }catch(reason){if(!controller.signal.aborted){setError(reason instanceof Error?reason.message:'Sola could not respond.');setAssistantBusy(false);}}
  };

  const decideSuggestion = async (
    suggestion: SermonSuggestion,
    decision: "accepted" | "rejected",
  ) => {
    const response = await api<{ sermon?: SermonRecord }>(
      `/api/sermons/${sermon.id}/suggestions/${suggestion.id}/decision`,
      {
        method: "POST",
        body: JSON.stringify({
          decision,
          documentVersion: sermon.documentVersion,
        }),
      },
    );
    if (response.sermon) {
      setDetail((current) =>
        current
          ? {
              ...current,
              sermon: response.sermon!,
              suggestions: current.suggestions.map((item) =>
                item.id === suggestion.id ? { ...item, status: decision } : item,
              ),
            }
          : current,
      );
      await cacheSermon(response.sermon);
    } else {
      setDetail((current) =>
        current
          ? {
              ...current,
              suggestions: current.suggestions.map((item) =>
                item.id === suggestion.id ? { ...item, status: decision } : item,
              ),
            }
          : current,
      );
    }
  };

  const runReview = async () => {
    const response = await api<{
      findings: SermonReviewFinding[];
    }>(`/api/sermons/${sermon.id}/review`, {
      method: "POST",
      body: "{}",
    });
    setReviewFindings(response.findings);
    setReviewOpen(true);
  };

  const decideAiDraft = async (
    draft: SermonAiDraft,
    decision: "apply_all" | "add_selected" | "discard",
  ) => {
    if (!draft.proposedDocument && decision !== "discard") return;
    setDraftBusy(true);
    setError("");
    try {
      const edits = draftEdits[draft.id] ?? {};
      const editedDocument = draft.proposedDocument
        ? {
            ...draft.proposedDocument,
            blocks: draft.proposedDocument.blocks.map((block) =>
              edits[block.id] === undefined
                ? block
                : { ...block, content: createSermonBlock(block.type, edits[block.id], block).content },
            ),
          }
        : undefined;
      const response = await api<{ sermon?: SermonRecord; draft: SermonAiDraft }>(
        `/api/sermons/${sermon.id}/ai-drafts/${draft.id}/decision`,
        {
          method: "POST",
          body: JSON.stringify({
            decision,
            documentVersion: sermon.documentVersion,
            selectedBlockIds: draftSelections[draft.id] ?? [],
            editedDocument,
          }),
        },
      );
      setDetail((current) =>
        current
          ? {
              ...current,
              sermon: response.sermon ?? current.sermon,
              aiDrafts: current.aiDrafts.map((item) =>
                item.id === draft.id ? response.draft : item,
              ),
            }
          : current,
      );
      if (response.sermon) await cacheSermon(response.sermon);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "The proposal decision could not be saved.");
    } finally {
      setDraftBusy(false);
    }
  };

  const cancelAiDraft = async (draft: SermonAiDraft) => {
    setDraftBusy(true);
    try {
      await api(`/api/sermons/${sermon.id}/ai-drafts/${draft.id}/cancel`, {
        method: "POST",
        body: "{}",
      });
      setDetail((current) =>
        current
          ? {
              ...current,
              aiDrafts: current.aiDrafts.map((item) =>
                item.id === draft.id ? { ...item, status: "canceled" } : item,
              ),
            }
          : current,
      );
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "The proposal could not be canceled.");
    } finally {
      setDraftBusy(false);
    }
  };

  const linkNote = async (noteId: string) => {
    const response = await api<{ noteLinks: SermonStudyLink[] }>(
      `/api/sermons/${sermon.id}/note-links`,
      {
        method: "POST",
        body: JSON.stringify({ noteId, classification: "observation" }),
      },
    );
    setDetail((current) => (current ? { ...current, noteLinks: response.noteLinks } : current));
  };

  const createArtifact = async (kind: SermonArtifact["kind"]) => {
    setAssistantBusy(true);
    try {
      const label = kind === "handout" ? "editable listener handout" : "editable discussion guide";
      const response = await api<{ answer: string }>(`/api/sermons/${sermon.id}/assistant`, {
        method: "POST",
        body: JSON.stringify({
          prompt: `Generate a concise ${label} from this saved sermon. Use Markdown. Mark every AI-generated question or summary as draft material that the author must review.`,
          action: "ask",
          blockIds: [],
          organizationId: organizationId || undefined,
        }),
      });
      const artifactResponse = await api<{ artifact: SermonArtifact }>(
        `/api/sermons/${sermon.id}/artifacts`,
        {
          method: "POST",
          body: JSON.stringify({
            kind,
            title: `${sermon.title} — ${kind === "handout" ? "Handout" : "Discussion guide"}`,
            contentMarkdown: response.answer,
            status: "draft",
          }),
        },
      );
      setDetail((current) =>
        current
          ? { ...current, artifacts: [artifactResponse.artifact, ...current.artifacts] }
          : current,
      );
      setAssistantMessages((current) => [
        ...current,
        {
          role: "assistant",
          content: `${label[0]?.toUpperCase()}${label.slice(1)} saved as a reviewable draft.`,
        },
      ]);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "The artifact could not be created.");
    } finally {
      setAssistantBusy(false);
    }
  };

  const exportDocx = async () => {
    await saveRevision("export");
    const children = sermon.document.blocks.map((block) => {
      const text = plainTextFromRichText(block.content);
      const heading =
        block.type === "title"
          ? HeadingLevel.TITLE
          : block.type === "point"
            ? HeadingLevel.HEADING_1
            : block.type === "subpoint"
              ? HeadingLevel.HEADING_2
              : undefined;
      return new Paragraph({
        ...(heading ? { heading } : {}),
        children: [
          ...(heading
            ? []
            : [
                new TextRun({
                  text: `${blockLabels[block.type]}  `,
                  bold: true,
                  color: block.type === "scripture" ? "38513F" : "777167",
                }),
              ]),
          new TextRun({ text, italics: block.type === "stage_direction" }),
        ],
        spacing: { after: 180 },
      });
    });
    const document = new Document({
      sections: [
        {
          properties: {},
          children: [
            new Paragraph({
              children: [
                new TextRun({ text: sermon.passageLabel, bold: true }),
                new TextRun({ text: ` · ${sermon.primaryVersionId.toUpperCase()} · ${sermon.targetMinutes} minutes` }),
              ],
              spacing: { after: 280 },
            }),
            ...children,
          ],
        },
      ],
    });
    const blob = await Packer.toBlob(document);
    const url = URL.createObjectURL(blob);
    const anchor = window.document.createElement("a");
    anchor.href = url;
    anchor.download = `${sermon.title.replace(/[^\w -]/g, "").trim() || "sermon"}.docx`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const manuscript = sermon.document.blocks
    .filter((block) => block.type !== "private_note")
    .map((block) => ({ ...block, text: plainTextFromRichText(block.content) }))
    .filter((block) => block.text);
  const outline = manuscript.filter((block) =>
    ["title", "big_idea", "point", "subpoint", "scripture", "application"].includes(block.type),
  );

  return (
    <section className="sermon-builder">
      <header className="sermon-builder-header">
        <div className="sermon-builder-title">
          <Link to="/sermons" className="icon-button" aria-label="Back to sermons"><ArrowLeft size={20} /></Link>
          <div>
            <input
              aria-label="Sermon title"
              value={sermon.title}
              onChange={(event) => updateLocal((current) => ({ ...current, title: event.target.value }))}
            />
            <p>
              {sermon.passageLabel || "No passage"} · {sermon.primaryVersionId.toUpperCase()} ·{" "}
              <span className={`save-state save-${saveState}`}>
                {saveState === "saving" ? "Saving…" : saveState === "offline" ? "Saved offline" : saveState === "conflict" ? "Conflict preserved" : "Saved"}
              </span>
            </p>
          </div>
        </div>
        <div className="sermon-builder-header-actions sermon-builder-actions-desktop">
          <button type="button" className="ghost-button" onClick={() => void saveRevision()}>
            <Check size={17} /> Save revision
          </button>
          <button type="button" className="ghost-button" onClick={() => window.print()}>
            <Printer size={17} /> Print / PDF
          </button>
          <button type="button" className="ghost-button" onClick={() => void exportDocx()}>
            <FileDoc size={17} /> DOCX
          </button>
          <Link className="primary-button" to={`/sermons/${sermon.id}/preach`}>
            <Play size={17} weight="fill" /> Preach
          </Link>
        </div>
        <div className="sermon-builder-header-actions sermon-builder-actions-mobile">
          <details className="sermon-builder-more-actions">
            <summary className="ghost-button">
              <DotsThree size={20} weight="bold" /> More
            </summary>
            <div>
              <button type="button" onClick={() => void saveRevision()}>
                <Check size={17} /> Save revision
              </button>
              <button type="button" onClick={() => window.print()}>
                <Printer size={17} /> Print / PDF
              </button>
              <button type="button" onClick={() => void exportDocx()}>
                <FileDoc size={17} /> Export DOCX
              </button>
            </div>
          </details>
          <Link className="primary-button" to={`/sermons/${sermon.id}/preach`}>
            <Play size={17} weight="fill" /> Preach
          </Link>
        </div>
      </header>

      <div className="sermon-metadata-bar">
        <label>
          <span>Audience</span>
          <input value={sermon.audience} onChange={(event) => updateLocal((current) => ({ ...current, audience: event.target.value }))} />
        </label>
        <label>
          <span>Target</span>
          <input type="number" min={1} max={600} value={sermon.targetMinutes} onChange={(event) => updateLocal((current) => ({ ...current, targetMinutes: Number(event.target.value) }))} />
        </label>
        <label>
          <span>Status</span>
          <select value={sermon.status} onChange={(event) => updateLocal((current) => ({ ...current, status: event.target.value as SermonStatus }))}>
            <option value="idea">Idea</option><option value="draft">Draft</option><option value="ready">Ready</option><option value="preached">Preached</option><option value="archived">Archived</option>
          </select>
        </label>
        <div className="sermon-live-stats">
          <span>{wordCount.toLocaleString()} words</span>
          <span>{estimatedMinutes} min estimate</span>
          <span>{sermon.document.blocks.length} blocks</span>
        </div>
      </div>

      {sermonResearchId ? <section className="sermon-research-workspace">
        <button type="button" className="ghost-button" onClick={()=>setSermonResearchId(undefined)}>Close research</button>
        <StudyWorkspace runId={sermonResearchId}/>
      </section> : null}
      {[...new Set(sermon.document.blocks.flatMap(block=>block.researchRunId?[block.researchRunId]:[]))].map(id=><button type="button" className="ghost-button" key={id} onClick={()=>setSermonResearchId(id)}>Open retained study sources</button>)}
      <section className="sermon-draft-workbench" aria-label="Sola sermon starters">
        <header>
          <div>
            <p className="eyebrow">Optional head start</p>
            <h2>Sola sermon starter</h2>
            <p>Generate a reviewable proposal. Nothing enters your sermon until you choose it.</p>
          </div>
          {!detail.aiDrafts.some((draft) => ["queued", "generating"].includes(draft.status)) ? (
            <div className="sermon-draft-launchers">
              <button
                type="button"
                className="secondary-button"
                disabled={draftBusy}
                onClick={() => void startAiDraft("quick", "full", "", organizationId || undefined)}
              >
                <Sparkle size={17} /> Sola Draft
              </button>
              <button
                type="button"
                className="secondary-button"
                disabled={draftBusy}
                onClick={() => void startAiDraft("deep", "full")}
              >
                <Books size={17} /> Deep Research + Draft
              </button>
            </div>
          ) : null}
        </header>
        {draftBusy && !detail.aiDrafts.length ? (
          <div className="sermon-draft-status"><SolaMark decorative /><p>Sola is preparing the durable proposal card…</p></div>
        ) : null}
        {detail.aiDrafts
          .filter((draft) => draft.status !== "rejected")
          .slice(0, 3)
          .map((draft) => {
            const selected = new Set(draftSelections[draft.id] ?? []);
            const replacementAllowed = sermon.documentVersion === draft.baseDocumentVersion;
            return (
              <article key={draft.id} className={`sermon-ai-draft-card draft-${draft.status}`}>
                <header>
                  <div>
                    <span>{draft.mode === "deep" ? "Deep Research + Draft" : "Sola Draft"}</span>
                    <h3>{draft.depth === "full" ? "Full first draft" : "Detailed outline"}</h3>
                  </div>
                  <span className="sermon-draft-state">{draft.status.replaceAll("_", " ")}</span>
                </header>
                {["queued", "generating"].includes(draft.status) ? (
                  <div className="sermon-draft-status" aria-live="polite">
                    <SolaMark decorative />
                    <div>
                      <strong>Sola is working in the background</strong>
                      <p>{draftProgress[draft.id] || (draft.mode === "deep" ? "Planning source-grounded research\u2026" : "Building the proposal\u2026")}</p>
                    </div>
                    <button type="button" className="ghost-button" disabled={draftBusy} onClick={() => void cancelAiDraft(draft)}>
                      Cancel
                    </button>
                  </div>
                ) : null}
                {draft.status === "failed" ? (
                  <div className="sermon-draft-failure">
                    <WarningCircle size={20} />
                    <div><strong>The blank sermon is safe.</strong><p>{draft.errorMessage || "The proposal was interrupted and unused credits were returned."}</p></div>
                    <button type="button" className="secondary-button" onClick={() => void startAiDraft(draft.mode, draft.depth, draft.researchFocus, organizationId || undefined)}>Retry</button>
                  </div>
                ) : null}
                {draft.status === "ready" && draft.proposedDocument ? (
                  <>
                    {draft.mode === "deep" && draft.deepStudyId ? (
                      <a
                        className="sermon-draft-research-link"
                        href={`/ask-sola?study=${encodeURIComponent(draft.deepStudyId)}`}
                      >
                        <Books size={17} />
                        Open the saved source-linked research
                      </a>
                    ) : null}
                    {draft.researchRunId ? <button type="button" className="ghost-button" onClick={()=>setSermonResearchId(draft.researchRunId!)}>View passage and research sources</button> : null}
                    {!replacementAllowed ? (
                      <p className="sermon-draft-conflict-note">
                        You edited the sermon after generation began. Your work is protected; add selected blocks instead of replacing it.
                      </p>
                    ) : null}
                    <div className="sermon-draft-preview">
                      {draft.proposedDocument.blocks.map((block) => {
                        const text = draftEdits[draft.id]?.[block.id] ?? plainTextFromRichText(block.content);
                        const label =
                          block.provenance === "scripture"
                            ? "Verified Scripture"
                            : block.provenance === "interpretation"
                              ? "Interpretation"
                              : block.type === "illustration"
                                ? "Illustration suggestion"
                                : block.type === "application"
                                  ? "Application"
                                  : block.provenance === "external_source"
                                    ? "External evidence"
                                    : "AI-generated prose";
                        return (
                          <article key={block.id} className={`sermon-draft-block provenance-${block.provenance}`}>
                            <header>
                              <label>
                                <input
                                  type="checkbox"
                                  checked={selected.has(block.id)}
                                  onChange={(event) => {
                                    const next = new Set(selected);
                                    if (event.target.checked) next.add(block.id);
                                    else next.delete(block.id);
                                    setDraftSelections((current) => ({ ...current, [draft.id]: [...next] }));
                                  }}
                                />
                                <strong>{blockLabels[block.type]}</strong>
                              </label>
                              <span>{label}</span>
                            </header>
                            <textarea
                              aria-label={`Edit proposed ${blockLabels[block.type]} block`}
                              value={text}
                              onChange={(event) =>
                                setDraftEdits((current) => ({
                                  ...current,
                                  [draft.id]: {
                                    ...(current[draft.id] ?? {}),
                                    [block.id]: event.target.value,
                                  },
                                }))
                              }
                            />
                            {block.sourceReferences?.map((source) => (
                              <small key={`${source.kind}:${source.reference}`}>
                                {source.reference} · {source.verified ? "verified" : "not verified as direct Scripture"}
                              </small>
                            ))}
                          </article>
                        );
                      })}
                    </div>
                    <footer>
                      <button type="button" className="ghost-button" disabled={draftBusy} onClick={() => void decideAiDraft(draft, "discard")}>Discard</button>
                      <button
                        type="button"
                        className="secondary-button"
                        disabled={draftBusy || !selected.size}
                        onClick={() => void decideAiDraft(draft, "add_selected")}
                      >
                        Add selected blocks ({selected.size})
                      </button>
                      <button
                        type="button"
                        className="primary-button"
                        disabled={draftBusy || !replacementAllowed}
                        onClick={() => void decideAiDraft(draft, "apply_all")}
                      >
                        Apply all
                      </button>
                    </footer>
                  </>
                ) : null}
                {draft.status === "accepted" ? (
                  <p className="sermon-draft-accepted"><Check size={18} /> Accepted in an immutable sermon revision.</p>
                ) : null}
              </article>
            );
          })}
      </section>

      <div className="sermon-mobile-tabs" role="tablist" aria-label="Sermon builder panels">
        {(["study", "sermon", "sola"] as const).map((tab) => (
          <button
            key={tab}
            type="button"
            role="tab"
            aria-selected={activeMobileTab === tab}
            className={activeMobileTab === tab ? "active" : ""}
            onClick={() => setActiveMobileTab(tab)}
          >
            {tab === "sola" ? "Sola" : tab[0]?.toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {saveState === "conflict" ? (
        <div className="sermon-conflict-banner">
          <WarningCircle size={20} />
          <p>Your version and the server version both remain preserved.</p>
          <button type="button" onClick={() => void saveConflictCopy()}>Save my work as a conflict copy</button>
        </div>
      ) : null}
      {error ? <div className="inline-error sermon-builder-error-line">{error}<button type="button" onClick={() => setError("")}><X size={16} /></button></div> : null}

      <div className="sermon-workspace">
        <aside className={`sermon-study-panel ${activeMobileTab === "study" ? "mobile-active" : ""}`}>
          <header>
            <p className="eyebrow">Study</p>
            <h2>Passage & notes</h2>
          </header>
          <article className="sermon-scripture-card">
            <span>Direct Scripture</span>
            <h3>{sermon.passageLabel || "Choose a passage"}</h3>
            <p>{sermon.primaryVersionId.toUpperCase()} · exact edition recorded</p>
            {sermon.passageLabel ? (
              <Link to={`/scripture/${encodeURIComponent(sermon.primaryReferences[0]?.book ?? "John")}/${sermon.primaryReferences[0]?.chapter ?? 3}`}>
                Read in Scripture
              </Link>
            ) : null}
          </article>
          <section className="sermon-linked-notes">
            <div className="panel-heading-row">
              <h3>Linked Study Notes</h3>
              <span>{detail.noteLinks.length}</span>
            </div>
            {detail.noteLinks.map((link) => (
              <article key={link.id}>
                <NotePencil size={18} />
                <div><strong>{link.noteTitle}</strong><span>{link.classification}</span></div>
              </article>
            ))}
            <label className="sermon-note-search">
              <MagnifyingGlass size={16} />
              <input value={noteQuery} onChange={(event) => setNoteQuery(event.target.value)} placeholder="Find a study note" />
            </label>
            <div className="sermon-note-results">
              {notes
                .filter((note) => !detail.noteLinks.some((link) => link.noteId === note.id))
                .slice(0, 5)
                .map((note) => (
                  <button type="button" key={note.id} onClick={() => void linkNote(note.id)}>
                    <Plus size={15} /> {note.title}
                  </button>
                ))}
            </div>
          </section>
          <section className="sermon-source-legend">
            <h3>Authority & provenance</h3>
            <span className="provenance provenance-scripture">Scripture</span>
            <span className="provenance provenance-user">Your writing</span>
            <span className="provenance provenance-interpretation">Interpretation</span>
            <span className="provenance provenance-ai_suggestion">AI accepted</span>
          </section>
        </aside>

        <main className={`sermon-canvas ${activeMobileTab === "sermon" ? "mobile-active" : ""}`}>
          <header className="sermon-canvas-toolbar">
            <div className="segmented-control">
              {(["blocks", "manuscript", "outline"] as const).map((value) => (
                <button type="button" key={value} className={view === value ? "active" : ""} onClick={() => setView(value)}>
                  {value}
                </button>
              ))}
            </div>
            <div className="sermon-add-block">
              <button className="secondary-button" type="button" onClick={() => setAddMenu((current) => !current)}>
                <Plus size={17} /> Add block
              </button>
              {addMenu ? (
                <div className="sermon-block-menu" role="menu">
                  {sermonBlockTypes.map((type) => (
                    <button role="menuitem" type="button" key={type} onClick={() => addBlock(type)}>
                      {blockLabels[type]}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          </header>

          {view === "blocks" ? (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={dragEnd}>
              <SortableContext items={sermon.document.blocks.map((block) => block.id)} strategy={verticalListSortingStrategy}>
                <div className="sermon-block-list">
                  {sermon.document.blocks.map((block) => (
                    <SortableSermonBlock
                      key={block.id}
                      block={block}
                      selected={selectedBlockIds.includes(block.id)}
                      onSelect={() =>
                        setSelectedBlockIds((current) =>
                          current.includes(block.id)
                            ? current.filter((idValue) => idValue !== block.id)
                            : [block.id],
                        )
                      }
                      onChange={changeBlock}
                      onDuplicate={() => {
                        const index = sermon.document.blocks.findIndex((item) => item.id === block.id);
                        const blocks = [...sermon.document.blocks];
                        blocks.splice(index + 1, 0, { ...block, id: crypto.randomUUID() });
                        updateDocument({ ...sermon.document, blocks });
                      }}
                      onDelete={() =>
                        updateDocument({
                          ...sermon.document,
                          blocks: sermon.document.blocks.filter((item) => item.id !== block.id),
                        })
                      }
                      onMove={(direction) => moveBlock(block.id, direction)}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          ) : null}

          {view === "manuscript" ? (
            <article className="sermon-manuscript">
              {manuscript.map((block) => (
                <section key={block.id} className={`manuscript-${block.type}`}>
                  {block.type !== "title" ? <span>{blockLabels[block.type]}</span> : null}
                  {block.type === "title" ? <h1>{block.text}</h1> : block.type === "point" ? <h2>{block.text}</h2> : <p>{block.text}</p>}
                </section>
              ))}
            </article>
          ) : null}

          {view === "outline" ? (
            <ol className="sermon-outline">
              {outline.map((block) => (
                <li key={block.id} className={`outline-${block.type}`}>
                  <span>{blockLabels[block.type]}</span>
                  <strong>{block.text}</strong>
                </li>
              ))}
            </ol>
          ) : null}
        </main>

        <aside className={`sermon-sola-panel ${activeMobileTab === "sola" ? "mobile-active" : ""}`}>
          <header>
            <SolaMark />
            <div><p className="eyebrow">Fallible AI assistance</p><h2>Ask Sola</h2></div>
          </header>
          <p className="sermon-sola-context">
            Pastor / Teacher mode · {selectedBlockIds.length ? `${selectedBlockIds.length} block selected` : "whole sermon context"}
          </p>
          {billing?.organizations?.length ? (
            <label className="sermon-org-balance">
              <span>Credit balance</span>
              <select value={organizationId} onChange={(event) => setOrganizationId(event.target.value)}>
                <option value="">My plan</option>
                {billing.organizations.map((organization) => (
                  <option value={organization.organizationId} key={organization.organizationId}>
                    {organization.name} · {organization.available} credits
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          <div className="sermon-action-chips">
            {assistantActions.map(([action, label]) => (
              <button
                key={action}
                type="button"
                disabled={assistantBusy}
                onClick={() =>
                  void askSola(
                    action,
                    `${label} for the selected sermon material. Preserve my voice and show only a proposed revision.`,
                  )
                }
              >
                {label}
              </button>
            ))}
          </div>
          <StudyWorkspace run={assistantResearch} answerRuns={assistantMessages.flatMap(message=>message.researchRun?[message.researchRun]:[])} selections={[{versionId:sermon.primaryVersionId as ScriptureSelection["versionId"],editionHash:sermon.editionHash,ranges:sermon.primaryReferences}]}><div className="sermon-sola-conversation" aria-live="polite">
            {assistantMessages.length ? assistantMessages.map((message, index) => (
              <article key={`${message.role}-${index}`} className={`sermon-sola-message ${message.role}`}>
                <span>{message.role === "assistant" ? "Sola · AI commentary" : "You"}</span>{message.researchRun?<button className="btn" onClick={()=>setAssistantResearch(message.researchRun!)}>Study this answer</button>:null}
                {message.role === "assistant" ? <MarkdownContent>{message.content}</MarkdownContent> : <p>{message.content}</p>}
              </article>
            )) : (
              <div className="sermon-sola-empty">
                <Sparkle size={26} />
                <p>Ask about the passage, outline, selected block, audience, or timing.</p>
              </div>
            )}
            {detail.suggestions.filter((item) => item.status === "pending").map((suggestion) => (
              <article className="sermon-suggestion" key={suggestion.id}>
                <span>Current</span>
                <p>{plainTextFromRichText(suggestion.currentContent) || "No selected block"}</p>
                <span>Suggested · AI-generated</span>
                <p>{plainTextFromRichText(suggestion.suggestedContent)}</p>
                <small>{suggestion.rationale}</small>{suggestion.researchRunId?<button className="btn" onClick={()=>setSermonResearchId(suggestion.researchRunId!)}>Study proposal sources</button>:null}
                <div>
                  <button type="button" className="primary-button" onClick={() => void decideSuggestion(suggestion, "accepted")}>
                    <Check size={15} /> Accept
                  </button>
                  <button type="button" className="ghost-button" onClick={() => void decideSuggestion(suggestion, "rejected")}>
                    Reject
                  </button>
                </div>
              </article>
            ))}
          </div>
          </StudyWorkspace>
          {assistantBusy?<div role="status" className="research-progress">{assistantStage}{assistantRunId?<button className="btn" onClick={()=>void api(`/api/research/runs/${assistantRunId}/cancel`,{method:'POST'}).then(()=>{assistantAbort.current?.abort();setAssistantBusy(false);setAssistantRunId(null);setAssistantStage('Canceled');}).catch(reason=>setError(String(reason)))}>Stop</button>:null}</div>:null}
          <form className="sermon-sola-composer" onSubmit={(event) => { event.preventDefault(); void askSola(); }}>
            <textarea
              value={assistantPrompt}
              onChange={(event) => setAssistantPrompt(event.target.value)}
              placeholder="Ask Sola about this sermon…"
              rows={3}
            />
            <button className="primary-button" disabled={assistantBusy || !assistantPrompt.trim()} type="submit">
              {assistantBusy ? "Sola is thinking…" : "Ask Sola"}
            </button>
          </form>
          <div className="sermon-output-actions">
            <button type="button" onClick={() => void runReview()}><WarningCircle size={17} /> Review sermon</button>
            <button type="button" onClick={() => void createArtifact("handout")}><FileDoc size={17} /> Handout</button>
            <button type="button" onClick={() => void createArtifact("discussion_guide")}><ListBullets size={17} /> Discussion guide</button>
          </div>
          {detail.artifacts.length ? (
            <div className="sermon-artifact-list">
              <h3>Saved outputs</h3>
              {detail.artifacts.map((artifact) => (
                <details key={artifact.id}>
                  <summary>{artifact.title}<span>{artifact.status}</span></summary>
                  <MarkdownContent>{artifact.contentMarkdown}</MarkdownContent>
                </details>
              ))}
            </div>
          ) : null}
        </aside>
      </div>

      {reviewOpen ? (
        <div className="modal-backdrop" role="presentation" onMouseDown={() => setReviewOpen(false)}>
          <section className="sermon-review-dialog" role="dialog" aria-modal="true" aria-labelledby="sermon-review-title" onMouseDown={(event) => event.stopPropagation()}>
            <button className="modal-close" type="button" aria-label="Close review" onClick={() => setReviewOpen(false)}><X size={20} /></button>
            <p className="eyebrow">Author review</p>
            <h2 id="sermon-review-title">These areas deserve another look</h2>
            <p>This review does not certify the sermon as correct or authoritative.</p>
            <div>
              {reviewFindings.map((finding) => (
                <article key={finding.id} className={`review-${finding.severity}`}>
                  <span>{finding.category}</span>
                  <h3>{finding.title}</h3>
                  <p>{finding.detail}</p>
                </article>
              ))}
            </div>
          </section>
        </div>
      ) : null}
    </section>
  );
}

export function SermonPreach({ user, onAuth }: SermonProps) {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const [sermon, setSermon] = useState<SermonRecord | null>(null);
  const [mode, setMode] = useState<"scroll" | "page">("scroll");
  const [theme, setTheme] = useState<"light" | "dark">("dark");
  const [fontSize, setFontSize] = useState(38);
  const [page, setPage] = useState(0);
  const [running, setRunning] = useState(true);
  const [elapsed, setElapsed] = useState(0);
  const [outlineOpen, setOutlineOpen] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const longPressTimer = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (!user) return;
    let active = true;
    void api<SermonDetailResponse>(`/api/sermons/${id}`)
      .then(async (response) => {
        if (!active) return;
        setSermon(response.sermon);
        await cacheSermon(response.sermon);
        const session = await api<{ sessionId: string }>(
          `/api/sermons/${id}/preaching-sessions`,
          {
            method: "POST",
            body: JSON.stringify({ action: "start", displayMode: mode }),
          },
        ).catch(() => null);
        if (session) setSessionId(session.sessionId);
      })
      .catch(async () => {
        const cached = await cachedSermon(id);
        if (active) setSermon(cached);
      });
    return () => {
      active = false;
    };
  }, [id, user]);

  useEffect(() => {
    if (!running) return;
    const timer = window.setInterval(() => setElapsed((value) => value + 1), 1_000);
    return () => window.clearInterval(timer);
  }, [running]);

  useEffect(() => {
    let wakeLock: WakeLockSentinel | null = null;
    void navigator.wakeLock?.request("screen").then((lock) => {
      wakeLock = lock;
    }).catch(() => undefined);
    return () => {
      void wakeLock?.release();
    };
  }, []);

  const blocks = useMemo(
    () =>
      sermon?.document.blocks.filter(
        (block) => block.includeInPreaching && plainTextFromRichText(block.content),
      ) ?? [],
    [sermon],
  );

  const finish = useCallback(async () => {
    if (sessionId) {
      await api(`/api/sermons/${id}/preaching-sessions`, {
        method: "POST",
        body: JSON.stringify({
          action: "finish",
          sessionId,
          elapsedSeconds: elapsed,
          displayMode: mode,
        }),
      }).catch(() => undefined);
    }
    navigate(`/sermons/${id}`);
  }, [elapsed, id, mode, navigate, sessionId]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "ArrowRight" || event.key === "PageDown") {
        setPage((value) => Math.min(blocks.length - 1, value + 1));
      } else if (event.key === "ArrowLeft" || event.key === "PageUp") {
        setPage((value) => Math.max(0, value - 1));
      } else if (event.key === " ") {
        event.preventDefault();
        setRunning((value) => !value);
      } else if (event.key === "Escape") {
        void finish();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [blocks.length, finish]);

  if (!user) return <SignedOutSermons onAuth={onAuth} />;
  if (!sermon) return <div className="sermon-preach sermon-preach-loading">Opening the offline preaching snapshot…</div>;

  const minutes = Math.floor(elapsed / 60);
  const seconds = String(elapsed % 60).padStart(2, "0");
  const remaining = Math.max(0, sermon.targetMinutes * 60 - elapsed);
  const progress = Math.min(100, Math.round((elapsed / (sermon.targetMinutes * 60)) * 100));
  const outline = blocks.filter((block) => ["point", "subpoint", "big_idea", "scripture"].includes(block.type));

  return (
    <section
      className={`sermon-preach sermon-preach-${theme} sermon-preach-${mode}`}
      style={{ "--preach-font-size": `${fontSize}px` } as React.CSSProperties}
    >
      <header>
        <button type="button" onClick={() => void finish()}><X size={22} /><span>Exit</span></button>
        <div>
          <strong>{sermon.title}</strong>
          <span>{sermon.passageLabel} · {sermon.primaryVersionId.toUpperCase()}</span>
        </div>
        <div className="preach-controls">
          <button type="button" onClick={() => setRunning((value) => !value)} aria-label={running ? "Pause timer" : "Resume timer"}>
            {running ? <Pause size={20} /> : <Play size={20} />}
          </button>
          <button type="button" onClick={() => setTheme((value) => value === "dark" ? "light" : "dark")} aria-label="Toggle light and dark theme">
            {theme === "dark" ? <Sun size={20} /> : <Moon size={20} />}
          </button>
          <button type="button" onClick={() => setFontSize((value) => Math.max(24, value - 4))} aria-label="Decrease text size">A−</button>
          <button type="button" onClick={() => setFontSize((value) => Math.min(72, value + 4))} aria-label="Increase text size">A+</button>
          <button type="button" onClick={() => setMode((value) => value === "scroll" ? "page" : "scroll")}>{mode === "scroll" ? "Page" : "Scroll"}</button>
        </div>
      </header>

      <div className="preach-progress"><span style={{ width: `${progress}%` }} /></div>

      <main>
        {mode === "scroll" ? (
          blocks.map((block, index) => (
            <article key={block.id} className={`preach-block preach-${block.type}`}>
              <span>{blockLabels[block.type]}</span>
              {block.type === "title" ? <h1>{plainTextFromRichText(block.content)}</h1> : block.type === "point" ? <h2>{plainTextFromRichText(block.content)}</h2> : <p>{plainTextFromRichText(block.content)}</p>}
              {index < blocks.length - 1 ? <small>Next: {plainTextFromRichText(blocks[index + 1]?.content).slice(0, 100)}</small> : null}
            </article>
          ))
        ) : (
          <article className={`preach-page preach-${blocks[page]?.type ?? "point"}`}>
            <span>{blockLabels[blocks[page]?.type ?? "point"]}</span>
            <p>{plainTextFromRichText(blocks[page]?.content)}</p>
            {blocks[page + 1] ? <small>Next: {plainTextFromRichText(blocks[page + 1]?.content).slice(0, 120)}</small> : null}
          </article>
        )}
      </main>

      <footer>
        <div><strong>{minutes}:{seconds}</strong><span>{Math.floor(remaining / 60)} min remaining</span></div>
        <button
          type="button"
          onPointerDown={() => {
            longPressTimer.current = window.setTimeout(() => setOutlineOpen(true), 600);
          }}
          onPointerUp={() => {
            if (longPressTimer.current) window.clearTimeout(longPressTimer.current);
          }}
          onClick={() => setOutlineOpen(true)}
        >
          <ListBullets size={20} /> Hold for outline
        </button>
        {mode === "page" ? (
          <div>
            <button type="button" onClick={() => setPage((value) => Math.max(0, value - 1))}><ArrowLeft size={24} /></button>
            <span>{page + 1} / {blocks.length}</span>
            <button type="button" onClick={() => setPage((value) => Math.min(blocks.length - 1, value + 1))}><ArrowRight size={24} /></button>
          </div>
        ) : null}
      </footer>

      {outlineOpen ? (
        <div className="preach-outline-overlay" role="dialog" aria-modal="true" aria-label="Emergency outline">
          <button type="button" onClick={() => setOutlineOpen(false)}><X size={22} /> Close</button>
          <h2>Emergency outline</h2>
          <ol>
            {outline.map((block) => <li key={block.id}>{plainTextFromRichText(block.content)}</li>)}
          </ol>
        </div>
      ) : null}
    </section>
  );
}
